import { Component, TemplateFunction, ScopeBase } from '@ribajs/core'
import type {
  // ShopifyCustomerAddress,
  ShopifyShippingRates,
  ShopifyShippingRatesNormalized,
} from '@ribajs/shopify'
import { ShopifyCartService, AnalyticsService } from '../services/index.js'
import { ShopifyCartObject, ShopifyCartLineItem } from '../types/index.js'

export interface Scope extends ScopeBase {
  cart: ShopifyCartObject | null
  // shippingAddress: ShopifyCustomerAddress | null
  estimateShippingRate: boolean
  shippingRates: ShopifyShippingRates | ShopifyShippingRatesNormalized
  continue: SbCartComponent['continue']
  onSubmit: SbCartComponent['onSubmit']
  getItem: SbCartComponent['getItem']
  pending: boolean
}

export class SbCartComponent extends Component {
  public static tagName = 'sb-cart'

  lifecycleOptions = {
    ignore: true,
  }

  static get observedAttributes(): string[] {
    return ['shipping-address', 'estimate-shipping-rate']
  }

  protected requiredAttributes(): string[] {
    return []
  }

  public scope: Scope = this.getScopeDefaults()

  protected getScopeDefaults(): Scope {
    return {
      cart: null,
      // shippingAddress: null,
      estimateShippingRate: false,
      shippingRates: [],
      continue: this.continue.bind(this),
      onSubmit: this.onSubmit.bind(this),
      getItem: this.getItem.bind(this),
      pending: false,
    }
  }

  set pending(value: boolean) {
    this.scope.pending = value
  }

  get pending() {
    return this.scope.pending
  }

  protected async setCart(cart: ShopifyCartObject | null) {
    if (this.scope.cart !== cart) {
      this.scope.cart = cart
    }
  }

  protected getCart(): ShopifyCartObject | null {
    return this.scope.cart || null
  }

  constructor() {
    super()
  }

  protected connectedCallback() {
    super.connectedCallback()
    this.init(SbCartComponent.observedAttributes)
  }

  /**
   * Remove item from the cart without waiting or making a Shopify request
   * @param lineItem
   */
  public removeLocalItem(lineItem: ShopifyCartLineItem) {
    if (this.scope.cart?.items.length) {
      this.scope.cart.items = this.scope.cart.items.filter(
        item => item.key !== lineItem.key,
      )
    }
  }

  /** Continue shopping */
  public continue(event?: Event) {
    // If there is a history...
    if (window.history.length > 0) {
      event?.preventDefault()
      window.history.back()
      return
    }
    console.warn('No history the user could go back to!')
  }

  public async onSubmit(/*event: Event, scope: Scope, formEl: HTMLFormElement*/) {
    if (!this.scope.cart) {
      throw new Error('Cart object not set!')
    }

    await ShopifyCartService.updateItems()
    await AnalyticsService.getInstance()?.trackBeginCheckout(this.scope.cart)
  }

  /**
   * Get line item by id
   * @param id
   */
  public getItem(id: number | string): ShopifyCartLineItem | null {
    id = Number(id)
    const item =
      this.scope.cart?.items.find(item => Number(item.id) === id) || null
    this.debug('getItem', id, item, this.scope.cart?.items)
    return item
  }

  protected onCartRequestStart() {
    this.scope.pending = true
  }

  protected async onCartRequestComplete(cart: ShopifyCartObject) {
    if (cart) {
      await this.setCart(cart)
    }
    this.scope.pending = false
  }

  protected async beforeBind() {
    await super.beforeBind()
    this.addEventListeners()
    await this.initCart()
    this.removePrerenderedElements()
  }

  protected async initCart() {
    if (!this.getCart()) {
      await this.setCart(await ShopifyCartService.get())
    }
  }

  /**
   * We are mixing the Shopify liquid template engine and the Riba.js template engines here to prerender the cart before Riba is ready.
   * This method is used to remove the prerendered items as soon as Riba.js is ready.
   */
  protected removePrerenderedElements() {
    const prerenderedEls = Array.from(this.querySelectorAll('[prerendered]'))
    for (const prerenderedEl of prerenderedEls) {
      prerenderedEl.remove()
    }
  }

  protected async afterBind() {
    this.debug('afterBind', this.scope)
    this.classList.add(`${SbCartComponent.tagName}-ready`)
    await super.afterBind()
  }

  protected disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListeners()
  }

  protected addEventListeners() {
    ShopifyCartService.events.on(
      'ShopifyCart:request:start',
      this.onCartRequestStart,
      this,
    )

    ShopifyCartService.events.on(
      'ShopifyCart:request:complete',
      this.onCartRequestComplete,
      this,
    )
  }

  protected removeEventListeners() {
    ShopifyCartService.events.off(
      'ShopifyCart:request:start',
      this.onCartRequestStart,
      this,
    )

    ShopifyCartService.events.off(
      'ShopifyCart:request:complete',
      this.onCartRequestComplete,
      this,
    )
  }

  protected template(): ReturnType<TemplateFunction> {
    return null
  }
}
